/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { axiosInstance } from './axios';

export interface DifyAPIKey {
  id: string;
  api_key_prefix: string;
  name: string;
  bot_id: string;
  space_id: string;
  creator_id: string;
  status: number;
  expired_at: number;
  last_used_at: number;
  created_at: number;
  updated_at: number;
}

export interface CreateDifyAPIKeyRequest {
  name: string;
  bot_id: string;
  space_id: string;
  expired_at?: number;
}

export interface CreateDifyAPIKeyResponse {
  code: number;
  msg: string;
  data?: {
    api_key: DifyAPIKey;
    raw_api_key: string;
  };
}

export interface ListDifyAPIKeysRequest {
  bot_id: string;
}

export interface ListDifyAPIKeysResponse {
  code: number;
  msg: string;
  data?: {
    api_keys: DifyAPIKey[];
  };
}

export interface DeleteDifyAPIKeyRequest {
  key_id: string;
  bot_id: string;
}

export interface DeleteDifyAPIKeyResponse {
  code: number;
  msg: string;
}

class DifyApiService {
  async createAPIKey(
    req: CreateDifyAPIKeyRequest,
  ): Promise<CreateDifyAPIKeyResponse> {
    // Note: axiosInstance interceptor already returns response.data
    const response = await axiosInstance.post<CreateDifyAPIKeyResponse>(
      '/api/dify/api_key/create',
      req,
    );
    return response as unknown as CreateDifyAPIKeyResponse;
  }

  async listAPIKeys(
    req: ListDifyAPIKeysRequest,
  ): Promise<ListDifyAPIKeysResponse> {
    // Note: axiosInstance interceptor already returns response.data
    const response = await axiosInstance.post<ListDifyAPIKeysResponse>(
      '/api/dify/api_key/list',
      req,
    );
    return response as unknown as ListDifyAPIKeysResponse;
  }

  async deleteAPIKey(
    req: DeleteDifyAPIKeyRequest,
  ): Promise<DeleteDifyAPIKeyResponse> {
    // Note: axiosInstance interceptor already returns response.data
    const response = await axiosInstance.post<DeleteDifyAPIKeyResponse>(
      '/api/dify/api_key/delete',
      req,
    );
    return response as unknown as DeleteDifyAPIKeyResponse;
  }
}

export const difyApi = new DifyApiService();
